import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowRight,
  Clock,
  Store,
  HandHelping,
  Handshake,
  Repeat,
  ShieldCheck,
  Sparkles,
  Compass,
} from "lucide-react";

const CAN_DO = [
  {
    icon: HandHelping,
    color: "var(--cm-primary)",
    title: "Post an opportunity",
    body: "Moving day, a community garden, an event that needs hands, help with a project — post it and let neighbors show up.",
    testid: "market-cando-post",
  },
  {
    icon: Handshake,
    color: "var(--cm-secondary)",
    title: "Pick up an opportunity",
    body: "Browse what neighbors need and lend a hand. Every verified hour you give lands in your balance as a Time Hour.",
    testid: "market-cando-pickup",
  },
  {
    icon: Store,
    color: "#B37A2C",
    title: "Offer goods & services",
    body: "Tutoring, cooking, repairs, ceramics, tech help, a spare bike — list it and earn Time Hours when someone redeems it.",
    testid: "market-cando-offer",
  },
  {
    icon: Repeat,
    color: "#7C4A6B",
    title: "Redeem your Time Hours",
    body: "Spend what you've earned on what your neighbors offer. One hour given is one hour you can receive.",
    testid: "market-cando-redeem",
  },
];

export default function MarketLanding() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const joinTo = isAuthed ? "/dashboard" : "/register?entry=market";

  return (
    <Layout>
      <div data-testid="market-landing-page" className="bg-app">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7 fade-up">
              <span className="overline text-[var(--cm-primary)]" data-testid="market-hero-overline">
                Bahoi — a community time bank
              </span>
              <h1
                data-testid="market-hero-headline"
                className="font-display font-bold mt-3 leading-[1.02] text-4xl sm:text-5xl lg:text-6xl"
              >
                Give an hour.
                <br />
                <span className="italic font-light">Receive an hour.</span>
                <br />
                Support each other.
              </h1>
              <p
                data-testid="market-hero-sub"
                className="text-ink-soft mt-6 text-base md:text-lg max-w-xl leading-relaxed"
              >
                Bahoi is a place where neighbors trade time instead of money. Post an
                opportunity when you need help, pick one up when you can give it, and offer
                or redeem goods and services — all paid in{" "}
                <span className="font-semibold text-ink">Time Hours</span>.
              </p>
              <p
                data-testid="market-hero-tagline"
                className="mt-4 text-sm md:text-base max-w-xl italic text-[var(--cm-primary)]"
              >
                Everyone has something to give. Everyone needs a hand sometimes.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {isAuthed ? (
                  <>
                    <Link to="/dashboard" data-testid="market-cta-dashboard" className="btn-primary inline-flex items-center gap-2">
                      Go to my dashboard
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to="/marketplace" data-testid="market-cta-browse" className="btn-outline">
                      Browse the marketplace
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to={joinTo} data-testid="market-cta-join" className="btn-primary inline-flex items-center gap-2">
                      Join the time bank
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to="/login" data-testid="market-cta-sign-in" className="btn-outline">
                      I already have an account
                    </Link>
                    <div className="time-badge ml-2">
                      <Clock className="h-3.5 w-3.5" />
                      +2 hours when you sign up
                    </div>
                  </>
                )}
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                <Stat n="1:1" label="hour for hour" />
                <Stat n="0" label="dollars needed" />
                <Stat n="∞" label="ways to help" />
              </div>
            </div>

            {/* Bento collage */}
            <div className="md:col-span-5 md:pl-4 fade-up">
              <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[460px]">
                <div className="col-span-4 row-span-4 rounded-2xl overflow-hidden border border-ink-soft">
                  <img
                    alt="Friends with arms around each other looking out together"
                    className="w-full h-full object-cover"
                    src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=900&q=80"
                  />
                </div>
                <div className="col-span-2 row-span-3 rounded-2xl bg-[var(--cm-secondary)] text-white p-4 flex flex-col justify-between">
                  <span className="overline opacity-80">Time Hour</span>
                  <div>
                    <div className="font-display text-4xl font-bold leading-none">1:1</div>
                    <p className="text-xs opacity-80 mt-1">
                      An hour of your time is worth exactly an hour of anyone else&apos;s.
                    </p>
                  </div>
                </div>
                <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden border border-ink-soft">
                  <img
                    alt="Neighbors working together around a table"
                    className="w-full h-full object-cover"
                    src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=600&q=80"
                  />
                </div>
                <div className="col-span-4 row-span-2 rounded-2xl bg-[var(--cm-accent)] p-4 flex items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-white/40 grid place-items-center text-[var(--cm-ink)]">
                    <Handshake className="h-5 w-5" />
                  </span>
                  <p className="font-display font-semibold text-[var(--cm-ink)] leading-tight">
                    Supported by neighbors,<br />supporting neighbors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Two doors strip */}
        <section data-testid="market-two-doors" className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 md:-mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[var(--cm-primary)]/10 border border-[var(--cm-primary)]/30 px-6 py-5 flex items-center justify-between gap-4">
              <p className="text-sm md:text-base text-[var(--cm-ink)]">
                <span className="font-display font-semibold">Need a hand?</span>{" "}
                <span className="text-ink-soft">Post what you need and let neighbors pick it up.</span>
              </p>
              <Link
                to={isAuthed ? "/opportunities/new" : joinTo}
                data-testid="market-need-help-cta"
                className="text-sm font-semibold text-[var(--cm-primary)] whitespace-nowrap inline-flex items-center gap-1 hover:underline underline-offset-4"
              >
                Post it <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="rounded-2xl bg-[var(--cm-secondary)]/10 border border-[var(--cm-secondary)]/30 px-6 py-5 flex items-center justify-between gap-4">
              <p className="text-sm md:text-base text-[var(--cm-ink)]">
                <span className="font-display font-semibold">Have something to give?</span>{" "}
                <span className="text-ink-soft">Offer a skill, a service, or a thing you no longer need.</span>
              </p>
              <Link
                to={isAuthed ? "/marketplace/new" : joinTo}
                data-testid="market-can-help-cta"
                className="text-sm font-semibold text-[var(--cm-secondary)] whitespace-nowrap inline-flex items-center gap-1 hover:underline underline-offset-4"
              >
                Offer it <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-surface border-y border-ink-soft mt-16" data-testid="market-how-it-works">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
            <span className="overline text-[var(--cm-primary)]">How the time bank works</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 max-w-2xl tracking-tight">
              Four steps. No money changes hands.
            </h2>
            <div className="grid md:grid-cols-4 gap-6 md:gap-8 mt-12">
              <Step n="01" title="Join" body="Create a free account and start with 2 Time Hours in your balance — a head start to spend right away." />
              <Step n="02" title="Give or offer" body="Pick up an opportunity a neighbor posted, or list a good or service you can provide." />
              <Step n="03" title="Get verified" body="The person you helped confirms it. Your Time Hours are credited — honest, simple, 1:1." />
              <Step n="04" title="Redeem" body="Spend your hours on anything neighbors offer: a meal, a lesson, a repair, a ride, a hand." />
            </div>
          </div>
        </section>

        {/* What you can do */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-20" data-testid="market-can-do">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="overline text-[var(--cm-primary)]">What you can do here</span>
              <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
                Two ways to give. Two ways to receive.
              </h2>
            </div>
            <Link
              to={isAuthed ? "/marketplace" : "/marketplace"}
              data-testid="market-cando-browse"
              className="btn-secondary text-sm"
            >
              Peek at the marketplace →
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {CAN_DO.map((c) => (
              <div key={c.title} data-testid={c.testid} className="card-soft p-7 hover:-translate-y-0.5 transition-transform">
                <div className="flex items-start gap-4">
                  <span
                    className="h-11 w-11 rounded-full grid place-items-center text-white flex-shrink-0"
                    style={{ background: c.color }}
                  >
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-xl leading-tight">{c.title}</h3>
                    <p className="text-ink-soft mt-2 leading-relaxed text-sm">{c.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Trust + assessment cross-link */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20" data-testid="market-trust-section">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="card-soft p-7 border-l-4 border-[var(--cm-secondary)]">
              <div className="flex items-start gap-4">
                <span className="h-11 w-11 rounded-full bg-[var(--cm-secondary)]/15 grid place-items-center flex-shrink-0">
                  <ShieldCheck className="h-5 w-5 text-[var(--cm-secondary)]" />
                </span>
                <div>
                  <h3 className="font-display font-semibold text-xl leading-tight">Built on trust</h3>
                  <p className="text-ink-soft mt-2 leading-relaxed text-sm">
                    Hours are only credited once the person you helped confirms it. Every member
                    has a visible trust score built from verified shifts and completed trades.
                  </p>
                </div>
              </div>
            </div>
            <div className="card-soft p-7 border-l-4 border-[var(--cm-primary)]">
              <div className="flex items-start gap-4">
                <span className="h-11 w-11 rounded-full bg-[var(--cm-primary)]/15 grid place-items-center flex-shrink-0">
                  <Compass className="h-5 w-5 text-[var(--cm-primary)]" />
                </span>
                <div>
                  <h3 className="font-display font-semibold text-xl leading-tight">Curious where you fit?</h3>
                  <p className="text-ink-soft mt-2 leading-relaxed text-sm">
                    Bahoi also matches people with causes they care about through a short
                    assessment. It&apos;s optional — take it anytime from your dashboard.
                  </p>
                  <Link
                    to="/how-it-works"
                    data-testid="market-assessment-link"
                    className="text-sm font-semibold text-[var(--cm-primary)] inline-flex items-center gap-1.5 mt-4 hover:underline underline-offset-4"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Learn about cause matching →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
          <div
            data-testid="market-final-cta"
            className="rounded-3xl bg-[var(--cm-ink)] text-[var(--cm-surface)] px-8 md:px-16 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center"
          >
            <div>
              <span className="overline text-[var(--cm-accent)]">
                {isAuthed ? "Keep going" : "Join your neighbors"}
              </span>
              <h2 className="font-display font-bold text-3xl md:text-5xl mt-3 leading-tight tracking-tight">
                {isAuthed ? "Someone nearby could use an hour." : "Your first two hours are on us."}
              </h2>
              <p className="text-white/70 mt-4 max-w-md">
                {isAuthed
                  ? "Post what you need, pick up what you can, or list something to offer. The bank grows every time you do."
                  : "Create an account and you'll have 2 Time Hours to spend right away — before you've given a single one."}
              </p>
            </div>
            <div className="md:justify-self-end flex flex-wrap gap-3">
              {isAuthed ? (
                <>
                  <Link to="/opportunities" data-testid="market-final-opportunities" className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2">
                    Find a way to help
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link to="/marketplace" data-testid="market-final-marketplace" className="btn-outline border-white text-white hover:bg-white hover:text-[var(--cm-ink)] text-lg px-6 py-4">
                    Marketplace
                  </Link>
                </>
              ) : (
                <Link to={joinTo} data-testid="market-final-join" className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2">
                  Join the time bank
                  <ArrowRight className="h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold">{n}</div>
      <div className="text-xs text-ink-soft uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="card-soft p-6">
      <span className="overline text-[var(--cm-primary)]">{n}</span>
      <h3 className="font-display font-semibold text-lg mt-2">{title}</h3>
      <p className="text-sm text-ink-soft mt-3 leading-relaxed">{body}</p>
    </div>
  );
}
